import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/cn';
import {
  formatValidityResetSummary,
  VALIDITY_RESET_MODE_OPTIONS,
  VALIDITY_RESET_SCOPE_OPTIONS,
  type ValidityResetMode,
  type ValidityResetResult,
  type ValidityResetScope,
} from '@/lib/entryValidityReset';

type Role = 'admin' | 'site_master';

type Props = {
  open: boolean;
  onClose: () => void;
  onDone?: (result: ValidityResetResult) => void;
  role: Role;
  /** 0 = 站长「全部站点」范围 */
  siteId: number;
  /** 展示用：当前站点配置天数 */
  defaultDays?: number;
  siteName?: string;
  /** 站长端更大字号与触控友好布局 */
  compactMobile?: boolean;
};

export function EntryValidityResetDialog({
  open,
  onClose,
  onDone,
  role,
  siteId,
  defaultDays,
  siteName,
  compactMobile,
}: Props) {
  const [scope, setScope] = useState<ValidityResetScope>('expired');
  const [mode, setMode] = useState<ValidityResetMode>('renew_from_today');
  const [includeSoldOut, setIncludeSoldOut] = useState(false);
  const [useCustomDays, setUseCustomDays] = useState(false);
  const [customDays, setCustomDays] = useState(defaultDays && defaultDays > 0 ? defaultDays : 100);
  const [preview, setPreview] = useState<ValidityResetResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [step, setStep] = useState<'setup' | 'confirm'>('setup');

  const action = role === 'admin' ? 'batch_reset_entry_validity' : 'site_master_batch_reset_entry_validity';

  useEffect(() => {
    if (!open) return;
    setScope('expired');
    setMode('renew_from_today');
    setIncludeSoldOut(false);
    setUseCustomDays(false);
    setCustomDays(defaultDays && defaultDays > 0 ? defaultDays : 100);
    setPreview(null);
    setErr('');
    setStep('setup');
    setBusy(false);
  }, [open, defaultDays]);

  const buildBody = useCallback(
    (dryRun: boolean) => {
      const body: Record<string, unknown> = {
        scope,
        mode,
        include_sold_out: includeSoldOut,
        dry_run: dryRun,
      };
      if (role === 'admin' || siteId > 0) {
        body.site_id = siteId;
      } else {
        body.site_id = 0;
      }
      if (useCustomDays) {
        body.days = Math.max(1, Math.min(3650, customDays || 1));
      }
      return body;
    },
    [scope, mode, includeSoldOut, role, siteId, useCustomDays, customDays],
  );

  async function runPreview() {
    setBusy(true);
    setErr('');
    try {
      const r = await apiFetch<ValidityResetResult>(action, {
        method: 'POST',
        body: JSON.stringify(buildBody(true)),
      });
      if (!r.ok) {
        setErr(r.detail || r.err || '预览失败');
        return;
      }
      setPreview(r);
      setStep('confirm');
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') return;
      setErr(e instanceof Error ? e.message : '预览失败');
    } finally {
      setBusy(false);
    }
  }

  async function runApply() {
    if (!preview) return;
    const matched = preview.matched ?? 0;
    if (matched < 1) {
      onClose();
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const r = await apiFetch<ValidityResetResult>(action, {
        method: 'POST',
        body: JSON.stringify(buildBody(false)),
      });
      if (!r.ok) {
        setErr(r.detail || r.err || '重置失败');
        return;
      }
      onDone?.(r);
      onClose();
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') return;
      setErr(e instanceof Error ? e.message : '重置失败');
    } finally {
      setBusy(false);
    }
  }

  const scopeHint = siteId > 0
    ? siteName
      ? `站点：${siteName}`
      : `站点 #${siteId}`
    : '范围：您管理的全部站点';

  const daysHint =
    useCustomDays
      ? `自定义 ${Math.max(1, customDays || 1)} 天`
      : defaultDays && defaultDays > 0
        ? `站点配置 ${defaultDays} 天`
        : '各站当前「展示有效期」配置';

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      title="批量重置展示有效期"
      wide={compactMobile ? false : true}
      footer={
        <div className={cn('flex flex-wrap gap-2', compactMobile ? 'flex-col' : 'justify-end')}>
          {step === 'confirm' ? (
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                className={compactMobile ? 'w-full py-3 text-base' : undefined}
                onClick={() => {
                  setStep('setup');
                  setPreview(null);
                }}
              >
                返回修改
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={busy}
                className={compactMobile ? 'w-full py-3 text-base' : undefined}
                onClick={() => void runApply()}
              >
                {busy
                  ? '执行中…'
                  : (preview?.matched ?? 0) < 1
                    ? '关闭'
                    : `确认重置 ${preview?.matched ?? 0} 条`}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                className={compactMobile ? 'w-full py-3 text-base' : undefined}
                onClick={onClose}
              >
                取消
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={busy || (role === 'admin' && siteId < 1)}
                className={compactMobile ? 'w-full py-3 text-base' : undefined}
                onClick={() => void runPreview()}
              >
                {busy ? '预览中…' : '预览影响范围'}
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className={cn('space-y-4', compactMobile ? 'text-base' : 'text-sm')}>
        <p className={cn('leading-relaxed text-slate-600', compactMobile && 'text-base')}>
          修改「展示有效期」配置<strong className="font-semibold text-slate-800">不会</strong>
          自动延长已入库信息。可用本功能按当前天数批量续期或对齐历史信息。
        </p>
        <p className="rounded-xl bg-slate-50 px-3 py-2 text-slate-700 ring-1 ring-slate-200/80">
          {scopeHint}
          <span className="mx-2 text-slate-300">·</span>
          {daysHint}
        </p>

        {step === 'setup' ? (
          <>
            <fieldset className="space-y-2">
              <legend className="mb-1 font-semibold text-slate-800">重置范围</legend>
              {VALIDITY_RESET_SCOPE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={cn(
                    'flex cursor-pointer gap-3 rounded-xl border px-3 py-2.5 transition',
                    scope === opt.value
                      ? 'border-teal-500 bg-teal-50/80 ring-1 ring-teal-500/30'
                      : 'border-slate-200 bg-white hover:border-slate-300',
                  )}
                >
                  <input
                    type="radio"
                    className="mt-1 accent-teal-600"
                    checked={scope === opt.value}
                    onChange={() => setScope(opt.value)}
                  />
                  <span className="min-w-0">
                    <span className="block font-semibold text-slate-900">{opt.label}</span>
                    <span className="mt-0.5 block text-xs leading-snug text-slate-500 sm:text-sm">
                      {opt.hint}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>

            <fieldset className="space-y-2">
              <legend className="mb-1 font-semibold text-slate-800">计期方式</legend>
              {VALIDITY_RESET_MODE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={cn(
                    'flex cursor-pointer gap-3 rounded-xl border px-3 py-2.5 transition',
                    mode === opt.value
                      ? 'border-teal-500 bg-teal-50/80 ring-1 ring-teal-500/30'
                      : 'border-slate-200 bg-white hover:border-slate-300',
                  )}
                >
                  <input
                    type="radio"
                    className="mt-1 accent-teal-600"
                    checked={mode === opt.value}
                    onChange={() => setMode(opt.value)}
                  />
                  <span className="min-w-0">
                    <span className="block font-semibold text-slate-900">{opt.label}</span>
                    <span className="mt-0.5 block text-xs leading-snug text-slate-500 sm:text-sm">
                      {opt.hint}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>

            <label className="flex items-start gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
              <input
                type="checkbox"
                className="mt-1 accent-teal-600"
                checked={useCustomDays}
                onChange={(e) => setUseCustomDays(e.target.checked)}
              />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-slate-900">临时指定天数</span>
                <span className="mt-0.5 block text-xs text-slate-500 sm:text-sm">
                  不勾选则使用站点当前配置（改配置后请先保存再重置）
                </span>
                {useCustomDays ? (
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    className="mt-2 w-28 rounded-lg border border-slate-200 px-2 py-1.5 text-base"
                    value={customDays}
                    onChange={(e) => setCustomDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  />
                ) : null}
              </span>
            </label>

            <label className="flex items-start gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
              <input
                type="checkbox"
                className="mt-1 accent-teal-600"
                checked={includeSoldOut}
                onChange={(e) => setIncludeSoldOut(e.target.checked)}
              />
              <span>
                <span className="block font-semibold text-slate-900">包含已售信息</span>
                <span className="mt-0.5 block text-xs text-slate-500 sm:text-sm">默认跳过已售，避免误把下架信息重新展示</span>
              </span>
            </label>
          </>
        ) : (
          <div className="space-y-3">
            <div className="rounded-2xl border border-teal-200 bg-teal-50/70 px-4 py-3">
              <p className="font-semibold text-teal-950">
                {formatValidityResetSummary(preview ?? { ok: true }, defaultDays)}
              </p>
              <p className="mt-1 text-xs text-teal-900/80 sm:text-sm">
                范围：{VALIDITY_RESET_SCOPE_OPTIONS.find((x) => x.value === scope)?.label} ·{' '}
                {VALIDITY_RESET_MODE_OPTIONS.find((x) => x.value === mode)?.label}
              </p>
            </div>
            {preview?.by_site && preview.by_site.length > 1 ? (
              <ul className="max-h-28 space-y-1 overflow-y-auto rounded-xl border border-slate-100 bg-slate-50/80 p-3 text-xs text-slate-700 sm:text-sm">
                {preview.by_site.map((s) => (
                  <li key={s.site_id} className="flex justify-between gap-2">
                    <span>站点 #{s.site_id}</span>
                    <span>
                      {s.matched} 条 · {s.valid_days} 天
                      {s.expired > 0 ? `（过期 ${s.expired}）` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            {preview?.samples && preview.samples.length > 0 ? (
              <div>
                <p className="mb-1.5 text-xs font-medium text-slate-500 sm:text-sm">样例预览</p>
                <ul className="max-h-40 space-y-2 overflow-y-auto">
                  {preview.samples.map((s) => (
                    <li
                      key={s.id}
                      className="rounded-xl border border-slate-100 bg-white px-3 py-2 text-xs sm:text-sm"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-slate-400">#{s.id}</span>
                        {s.is_expired ? (
                          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
                            已过期
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 line-clamp-2 text-slate-800">{s.title || '（无标题）'}</p>
                      <p className="mt-1 text-slate-500">
                        {s.valid_start_date} → {s.valid_end_date}（原 {s.valid_days} 天）
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-slate-500">没有匹配的信息，无需重置。</p>
            )}
            {(preview?.matched ?? 0) > 0 ? (
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-950 ring-1 ring-amber-200 sm:text-sm">
                此操作会立即改写数据库中的有效起止日，小程序目录将按新日期展示。请确认后再执行。
              </p>
            ) : null}
          </div>
        )}

        {err ? <p className="rounded-xl bg-rose-50 px-3 py-2 text-rose-800">{err}</p> : null}
      </div>
    </Modal>
  );
}
